import "./form-hidden-destination-selector.scss";
import { useState, useMemo, useCallback } from "react";
import { InputWrapper } from "../../../components";
import { useTranslate } from "../../../features/translation/hooks/use-translation";
import { DestinationSelectorModal } from "../../../components";
import Checkbox from "../../../components/inputs/checkbox/checkbox";
import Divider from "../../../components/divider/divider";
import { useDestinationSelector } from "../../../features/auto-form";

const FormHiddenDestinationSelector = (props) => {
  const { t } = useTranslate();
  const [isOpen, setIsOpen] = useState(false);

  const ds = useDestinationSelector(props);
  const {
    categories,
    destinations,
    expanded,
    setExpanded,
    disableCategories,
    disableDestinations,

    toggleCategory,
    toggleDestination,
    toggleAllInCountry,

    destKey,
    countryKey,
    allKey,
  } = ds;

  const language = props.market || "se";
  const { options = {} } = props;

  /** ---------- SORT COUNTRIES ---------- */
  const sortedOptions = useMemo(() => {
    const sortCountries = (list = []) =>
      [...list].sort((a, b) =>
        a[language]?.localeCompare(
          b[language],
          language === "se" ? "sv" : language
        )
      );

    return {
      europe: sortCountries(options.europe),
      asia: sortCountries(options.asia),
      northAmerica: sortCountries(options.northAmerica),
      southAmerica: sortCountries(options.southAmerica),

      africaMiddleEast: sortCountries([
        ...(options.africaMe || []),
        ...(options.middleEast || []),
      ]),
    };
  }, [options, language]);

  /** ---------- COUNTRY RENDERER ---------- */
  const renderCountry = useCallback(
    (country, idx) => {
      const cKey = countryKey(country);
      const aKey = allKey(country);

      const selectedCount = country.destinations.filter((d) =>
        destinations.includes(destKey(country, d))
      ).length;

      const isAll =
        destinations.includes(aKey) ||
        selectedCount === country.destinations.length;

      const note = isAll
        ? t("input.custom.hiddenDestinationSelector.all")
        : selectedCount > 0
        ? `${selectedCount} ${t(
            "input.custom.hiddenDestinationSelector.selected"
          )}`
        : "";

      return (
        <div
          key={`country-${cKey}-${idx}`}
          className={` accordion destination-label country ${
            disableDestinations ? "disabled" : ""
          }`}
        >
          <input
            type="radio"
            name="accordion"
            id={`acc_${cKey}`}
            checked={expanded === cKey}
            onClick={() => setExpanded(expanded === cKey ? "" : cKey)}
            readOnly
          />

          <label className="header" htmlFor={`acc_${cKey}`}>
            <span className="label">{country[language]}</span>
            <span className="notification">{note}</span>

            <span className="icon-accordion">
              <span
                className="icon"
                style={{
                  WebkitMaskImage:
                    "url(https://static.tui.com/assets/v2/icons/tui-light/chevron-down.svg)",
                  maskImage:
                    "url(https://static.tui.com/assets/v2/icons/tui-light/chevron-down.svg)",
                }}
              />
            </span>
          </label>

          <section className="body">
            <div className="content destination-options">
              {country.destinations.length > 1 && (
                <div
                  key={`all-${cKey}`}
                  style={{ padding: "var(--space-4)" }}
                >
                  <Checkbox
                    value={aKey}
                    checked={isAll}
                    label={`${country[language]} (${t(
                      "input.custom.hiddenDestinationSelector.all"
                    )})`}
                    onChange={() => toggleAllInCountry(country)}
                    disabled={disableDestinations}
                  />
                  <Divider className="thin passive" />
                </div>
              )}

              <div
                key={`dest-list-${cKey}`}
                style={{ paddingLeft: "var(--space-6)" }}
              >
                {country.destinations.map((d, dIdx) => {
                  const key = destKey(country, d);

                  return (
                    <Checkbox
                      key={`dest-${key}-${dIdx}`}
                      value={key}
                      checked={destinations.includes(key)}
                      label={d[language]}
                      onChange={(e) =>
                        toggleDestination(country, d, e.target.checked)
                      }
                      disabled={disableDestinations}
                    />
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      );
    },
    [
      expanded,
      destinations,
      disableDestinations,
      destKey,
      countryKey,
      allKey,
      toggleDestination,
      toggleAllInCountry,
      t,
      language,
      setExpanded,
    ]
  );

  
  /** ---------- CONTINENT RENDERER ---------- */
  const renderContinent = useCallback(
    (labelKey, countries, idx) => (
      <div key={`continent-${labelKey}-${idx}`} style={{padding: "var(--space-4)"}}>
        <Divider
          style={{ padding: "var(--space-4)" }}
          className={`horizontal thin primary invisible text-large ${
            disableDestinations ? "disabled" : ""
          }`}
          label={
            labelKey
              ? t(`input.custom.hiddenDestinationSelector.${labelKey}`)
              : "\u00A0"
          }
        />

        {countries.map((c, cIdx) => renderCountry(c, cIdx))}
      </div>
    ),
    [disableDestinations, renderCountry, t]
  );

  /** ---------- TEXT FOR INPUT ---------- */
  const textValue = useMemo(() => {
    const labels = [];

    labels.push(
      ...categories.map((c) =>
        t(`input.custom.hiddenDestinationSelector.${c}`)
      )
    );

    Object.values(options).forEach((countries) =>
      countries?.forEach((country) => {
        const aKey = allKey(country);

        if (destinations.includes(aKey)) {
          labels.push(
            `${country[language]} (${t(
              "input.custom.hiddenDestinationSelector.all"
            )})`
          );
          return;
        }

        country.destinations.forEach((d) => {
          const key = destKey(country, d);
          if (destinations.includes(key)) labels.push(d[language]);
        });
      })
    );

    return labels.join(", ");
  }, [categories, destinations, options, language, t]);

  return (
    <div>
      <InputWrapper
        {...props}
        label={t("input.custom.hiddenDestinationSelector.label")}
        displayError={ds.bind.displayError}
        error={ds.bind.error}
        inputType="text"
      >
        <div
          className="group input-hidden--field"
          data-label="input"
          onClick={() => setIsOpen(true)}
        >
          <input
            value={textValue}
            readOnly
            placeholder={t(
              "input.custom.hiddenDestinationSelector.placeholder"
            )}
          />

          {categories.length + destinations.length > 0 && (
<span
  className="hidden-field-count"
  style={{
    display: "flex",
    alignItems: "center",
    paddingRight: "var(--space-2)"
  }}
>
  ({categories.length + destinations.length})
</span>
          )}
        </div>
      </InputWrapper>

      <DestinationSelectorModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onReset={() => ds.bind.reset()}
        sortedOptions={sortedOptions}
        categories={categories}
        disableCategories={disableCategories}
        disableDestinations={disableDestinations}
        renderContinent={renderContinent}
        toggleCategory={toggleCategory}
      />
    </div>
  );
};

export default FormHiddenDestinationSelector;